import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { UsersRound, X, UserMinus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedUsersPopover({ sharedWith, resourceId, resourceType, onUnshare }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    if (loaded) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sharedUsers = response.data.filter((u) => sharedWith.includes(u.id));
      setUsers(sharedUsers);
      setLoaded(true);
    } catch {
      setUsers([]);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (!open) fetchUsers();
    setOpen(!open);
  };

  const handleUnshare = async (e, userId) => {
    e.stopPropagation();
    if (!resourceId || !resourceType) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/${resourceType}s/${resourceId}/unshare`, { user_ids: [userId] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User removed from sharing');
      if (onUnshare) onUnshare();
    } catch {
      toast.error('Failed to unshare');
    }
  };

  if (!sharedWith || sharedWith.length === 0) return null;

  return (
    <div className="relative inline-flex" ref={popoverRef}>
      <button
        onClick={handleClick}
        className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-primary rounded-full hover:bg-indigo-100 transition-colors"
        data-testid="btn-shared-users"
        title={`Shared with ${sharedWith.length} user${sharedWith.length !== 1 ? 's' : ''}`}
      >
        <UsersRound size={14} />
        <span className="text-xs font-medium">{sharedWith.length}</span>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-text-primary">Shared with</p>
            <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="text-text-muted hover:text-text-primary">
              <X size={14} />
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {users.length > 0 ? (
              users.map((u) => (
                <div key={u.id} className="px-4 py-2 flex items-center gap-2 group hover:bg-gray-50" data-testid={`shared-user-${u.id}`}>
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
                    {u.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-text-primary truncate">{u.full_name}</p>
                    <p className="text-xs text-text-muted truncate">{u.email}</p>
                  </div>
                  {resourceId && (
                    <button
                      onClick={(e) => handleUnshare(e, u.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-accent transition-all"
                      title="Remove access"
                      data-testid={`btn-unshare-${u.id}`}
                    >
                      <UserMinus size={14} />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-text-muted text-center py-3">Loading...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
